import { TutorialDefinition } from '../TutorialEngine.js';
import { SplittingRenderOptions } from './SplittingTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type SplittingScene = {
    gameState: string;
    renderOptions: SplittingRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Grid: 6/1-3:1C5,2D1,3B2
// Full solution: C0,D5,B1

const scenes: SplittingScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'C0,D5,B1',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Splitting, place splitters\nso beams hit all digits', cell: 'E3', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Beam entry, click to place splitter, digits split, dots = goes to wall
    // Empty: beam enters A0, exits right edge (nothing visible except entry line)
    // C0: beam splits south at C0, reaches digit 1 at C5, terminal dots where beams exit
    {
        gameState: 'F5',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Beam enters here\ntraveling east', cell: 'B1', anchor: 'center' },
            { type: 'text-label', text: 'Click to place\na splitter', cell: 'C0', anchor: 'right', color: COLORS.teal },
            { type: 'text-label', text: 'Digits split\nbeams too', cell: 'C4', anchor: 'left' },
            { type: 'text-label', text: 'Dots mean the beam\ngoes to the wall', cell: 'E4', anchor: 'near-above' },
            { type: 'pulse', cell: 'C0', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: ['F5', 'C0,F5'],
        },
    },

    // Scene 3: Badges show distance, teal = correct order, amber = wrong
    // Digit 1 (C5) at distance 7 → teal
    // Digit 3 (B2) at distance 11 → amber (wrong order)
    // Digit 2 (D1) at distance 14 → amber (wrong order)
    {
        gameState: 'E5,C0,B5,E1',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Badges show\ndistance traveled', cell: 'E2', anchor: 'center' },
            { type: 'text-label', text: 'Teal means\ncorrect order', cell: 'C4', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Amber means\nwrong order', cell: 'B3', anchor: 'center', color: COLORS.amber },
        ],
    },

    // Scene 4: Invalid states
    // Frame 1: E5,C0,E1,A5,A1 → digit 2 hit twice
    // Frame 2: C0,D5,E1,E5 → infinite loop (amber diamonds)
    {
        gameState: 'E5,C0,E1,A5,A1',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'A digit cannot\nbe hit twice', cell: 'E0', anchor: 'center', color: COLORS.red },
            { type: 'text-label', text: 'No infinite loops\n(amber = in loop)', cell: 'D4', anchor: 'center', color: COLORS.red },
        ],
        animate: {
            interval: 3000,
            loop: true,
            frames: ['E5,C0,E1,A5,A1', 'C0,D5,E1,E5'],
        },
    },

    // Scene 5: Splitters can be hit twice without loops
    {
        gameState: 'F5,C0,B5,E2,F2',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Splitters can be hit\ntwice from different\ndirections', cell: 'D4', anchor: 'near-above' },
        ],
    },

];

/**
 * Splitting tutorial: 5 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/splitting.md for detailed scene descriptions.
 */
export const SPLITTING_TUTORIAL: TutorialDefinition = {
    gameType: 'splitting',
    gridCode: '6/1-3:1C5,2D1,3B2',
    scenes,
};
